import { useState, useEffect } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

// Components
import NavLink from "./NavLink/NavLink";
import { Heading2 } from "../typography";
import { Body, Card, Header } from "../UI";
import BranchItem from "../lists/branches/Branch.item";
import NotificationsCounter from "../UI/NotificationCounter/NotificationCounter";

// Icons
import { Close, Logo, BranchesArrow, BellIcon } from "../../SVGS";

// Images
import Profile from "../../images/Profile.png";

// Actions
import { setActiveBranch } from "../../redux/actions/app.actions";
import { toggleNotificationsPanel } from "../../redux/actions/notifications.actions";

// Configs
import { generateMeniLinks } from "../../config/navigation.config";

// Constants
import { ANALYTICS } from "../../constants/menu-items/menu-items.constants";

// Services
import { enableScroll, disableScroll } from "../../utils";

//Utils
import { isEmpty } from "../../utils/global.utils";

// Hooks
import { useIsMobileLayout } from "../../hooks/useMediaQuery";
import { useOnboardingGate } from "../PageSpecificComponents/pagesComponents/dashboard/onboarding/useOnboardingStatus";

// Styles
import { SideMenuStyles } from "./sideMenu.styles";

const ACCOUNT_PATH = "/account-settings";

const SideMenu = ({ isOpen, onClose }) => {
  const location = useLocation();
  const history = useHistory();
  const { user, activeBranch, branches, menuItems } = useSelector(
    state => state.app
  );
  const { unreadCount, panelOpen: isNotifPanelOpen } = useSelector(
    state => state.notifications
  );
  // `=== false` so the padlock stays off while dashboardData loads.
  const analyticsLocked = useSelector(
    state => state.app.dashboardData?.accessToAnalytics === false
  );
  const dispatch = useDispatch();
  // While onboarding is incomplete only reached-step items are shown; routes
  // stay registered from the full menuItems so checklist deep-links still work.
  const gate = useOnboardingGate();
  const menuItemsToShow = gate.gating
    ? menuItems.filter(item => gate.menuAllowed(item.key))
    : menuItems;
  const isMobileLayout = useIsMobileLayout();

  const [isBranchesOpen, setIsBranchesOpen] = useState(false);

  const isOnAccountSettings = location.pathname === ACCOUNT_PATH;
  const selectedBranchExists = !isEmpty(branches) && !isEmpty(activeBranch);

  // Close the mobile drawer on any route change — covers NavLink taps, the
  // profile row's history.push and browser back/forward.
  useEffect(() => {
    if (isOpen) onClose?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const toggleBranches = () => {
    setIsBranchesOpen(prevState => !prevState);
    // On mobile the drawer owns the body scroll lock, so the branch overlay
    // must not toggle it (that would unlock the page behind the open drawer).
    if (isMobileLayout) return;
    if (isBranchesOpen) {
      enableScroll();
    } else {
      disableScroll();
    }
  };

  const closeBranches = () => {
    setIsBranchesOpen(false);
    if (!isMobileLayout) enableScroll();
  };

  const goToAccountSettings = () => {
    if (!isOnAccountSettings) {
      history.push({
        pathname: ACCOUNT_PATH,
        state: { from: location.pathname }
      });
    }
  };

  return (
    <SideMenuStyles $isOpen={isOpen}>
      <div className="sideMenu">
        <Logo className="logo" />
        {user && (
          <>
            <div
              className="brachContainer"
              onClick={() => (!isEmpty(branches) ? toggleBranches() : {})}
            >
              <p className="selectBranch">
                {selectedBranchExists && "Selected Branch"}
              </p>
              <div className="chooseBracnhContainer">
                <p className="chosenBranch">
                  {selectedBranchExists
                    ? `${activeBranch?.name}`
                    : "No Selected Branch"}
                </p>

                <div
                  className={`arrowContainer ${
                    isBranchesOpen && "arrowAnim"
                  }`}
                >
                  <BranchesArrow />
                </div>
              </div>
              <p className="address">
                {selectedBranchExists && `${activeBranch?.location?.address}`}
              </p>
            </div>
            <div className="linksContainer">
              {generateMeniLinks(menuItemsToShow).map((linkProperties, i) => (
                <NavLink
                  {...linkProperties}
                  isActive={
                    !isOnAccountSettings &&
                    location.pathname === linkProperties.path
                  }
                  locked={linkProperties.key === ANALYTICS && analyticsLocked}
                  onClick={onClose}
                  key={i}
                />
              ))}
            </div>
            <div className="notifSection">
              <div
                className={`notifRow ${isNotifPanelOpen ? "isActive" : ""}`}
                onClick={() => dispatch(toggleNotificationsPanel())}
              >
                <BellIcon size={20} />
                <span className="notifLabel">Notifications</span>
                <NotificationsCounter counter={unreadCount} />
              </div>
            </div>
            <div
              className={`profile ${isOnAccountSettings ? "isActive" : ""}`}
              onClick={goToAccountSettings}
            >
              <div className="imgContainer">
                <img src={Profile} alt="profile" />
              </div>
              <div className="profileSettings">
                <h5 className="userName">
                  {`${user?.firstName || ""} ${user?.lastName || ""}`}
                </h5>
                <p className="accSettings">Account Settings</p>
              </div>
            </div>
          </>
        )}
      </div>
      <div className={`branchesContainer ${isBranchesOpen ? "branchesOpen" : ""}`} onClick={closeBranches}>
        <div onClick={e => e.stopPropagation()}>
          <Card className="branchesCard">
            <Header className="branchesHeader">
              <Heading2>YOUR BRANCHES</Heading2>
              <Close onClick={closeBranches} />
            </Header>
            <Body className="branchesBody">
              {branches.map(branch => (
                <BranchItem
                  {...branch}
                  isActive={activeBranch?.id === branch.id}
                  key={branch.id}
                  onClick={() => {
                    dispatch(setActiveBranch(branch));
                    toggleBranches();
                  }}
                />
              ))}
            </Body>
          </Card>
        </div>
      </div>
    </SideMenuStyles>
  );
};

export default SideMenu;
